/**
 * Shloka Enhancer — auto-detects Sanskrit verses, scripture references, and
 * scripture-meaning structures in raw blog HTML and upgrades them into the
 * premium themed `.shloka` blockquote so legacy content matches the new
 * editorial standard.
 *
 * Pure DOM transformation (no LLM, no DB writes). Safe to run on every render.
 *
 * Detection (conservative, low false-positive):
 *   • A paragraph ending in `॥` (double danda) is the ANCHOR for a verse
 *   • From that anchor, we walk BACKWARD over adjacent paragraphs that look
 *     like verse continuation lines (Devanagari-heavy, no English prose words,
 *     end with `।` or no terminator, short) and ABSORB them as additional
 *     verse lines so a multi-line shloka split across <p> tags is captured
 *     as one card.
 *   • A `ॐ`-prefixed short paragraph (mantra invocation) also qualifies.
 *
 * Meaning / source absorption (forward):
 *   • Heading or <p><strong>label</strong></p> with text matching
 *     /हिन्दी अर्थ|भावार्थ|अर्थ|Meaning|Translation|Hindi Meaning/ → absorbed
 *     as `.meaning`.
 *   • A following `<p>` starting with an em-dash → absorbed as `.source`.
 *
 * Already-wrapped `<blockquote class="shloka">` is left untouched.
 */

// Strong verse anchor: paragraph with ॥
function hasVerseAnchor(text) {
  if (!text) return false;
  const t = text.trim();
  if (t.length < 4) return false;
  const dev = (t.match(/[\u0900-\u097F]/g) || []).length;
  const total = t.replace(/\s/g, '').length;
  if (total === 0 || dev / total < 0.5) return false;
  if (t.includes('॥')) return true;
  // Mantra invocation
  const stripped = t.replace(/^["'\u201c\u2018]+/, '');
  if (/^ॐ\s/.test(stripped) && t.length < 240) return true;
  return false;
}

// Soft check — "this paragraph looks like a verse line that belongs to the
// shloka above/below it" — short, mostly Devanagari, no English prose,
// usually ends with single danda `।` (line break inside a verse).
function looksLikeVerseLine(text) {
  if (!text) return false;
  const t = text.trim();
  if (t.length < 4 || t.length > 220) return false;
  // Exclude paragraphs that contain English prose words (likely meaning, not verse)
  const englishWords = (t.match(/\b[A-Za-z]{3,}\b/g) || []).length;
  if (englishWords > 0) return false;
  // Exclude labelled lines like "हिन्दी अर्थ:" or "अर्थ:" — those are meaning blocks
  if (/^(हिन्दी\s+अर्थ|अर्थ|भावार्थ|शास्त्रीय|व्याख्या|Translation|Meaning|Source)\s*[:।]/i.test(t)) return false;
  const dev = (t.match(/[\u0900-\u097F]/g) || []).length;
  const total = t.replace(/\s/g, '').length;
  if (total === 0) return false;
  const density = dev / total;
  // Verse lines are 80%+ Devanagari (no English mixed)
  return density >= 0.8 && (t.endsWith('।') || t.endsWith('॥') || /[\u0900-\u097F]$/.test(t));
}

// Forward meaning marker — either an h2-h6 OR a <p><strong>label</strong>...</p>
function isMeaningLabel(el) {
  if (!el) return false;
  const txt = (el.textContent || '').trim();
  return /^(हिन्दी\s+अर्थ|अर्थ|भावार्थ|Hindi\s+Meaning|Meaning|Translation)\s*[:.]/i.test(txt);
}

function isInterpretationLabel(el) {
  if (!el) return false;
  const txt = (el.textContent || '').trim();
  return /^(शास्त्रीय\s+व्याख्या|व्याख्या|Interpretation|Commentary)\s*[:.]/i.test(txt);
}

function isVerseIntroHeader(text) {
  if (!text) return false;
  const t = text.trim().toLowerCase();
  return (
    t.includes('authentic verse') ||
    t.includes('sanskrit verse') ||
    t.includes('scripture') ||
    t.includes('shloka') ||
    /^संस्कृत\s+श्लोक/i.test(text.trim()) ||
    /^श्लोक/i.test(text.trim()) ||
    /^मन्त्र/i.test(text.trim()) ||
    /^मंत्र/i.test(text.trim()) ||
    t.includes('प्रमाण')
  );
}

function isSourceLine(text) {
  if (!text) return false;
  const t = text.trim();
  return /^[—–-]\s*\S/.test(t) && t.length < 200;
}

/**
 * Transform raw HTML string → enhanced HTML with `.shloka` cards.
 */
export function enhanceShlokas(html) {
  if (!html || typeof html !== 'string') return html || '';
  if (typeof DOMParser === 'undefined') return html;

  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, 'text/html');
  } catch {
    return html;
  }
  const root = doc.getElementById('__root');
  if (!root) return html;
  // If auto-blog wraps everything in <article class="virat-blog">, operate on
  // its children so shloka detection reaches the H2 headings and paragraphs
  // inside the article body.
  const articleWrap = root.querySelector('article.virat-blog') || root.querySelector('article');
  const scope = articleWrap || root;

  const all = Array.from(scope.children);

  // Find anchors (paragraphs containing ॥ or ॐ-mantra) processed left-to-right.
  // Use a `consumed` set so absorbed elements are not re-processed.
  const consumed = new Set();

  for (let i = 0; i < all.length; i++) {
    const el = all[i];
    if (!el || consumed.has(el)) continue;
    if (el.tagName === 'BLOCKQUOTE' && el.classList.contains('shloka')) continue;

    const tag = (el.tagName || '').toLowerCase();
    if (tag !== 'p' && tag !== 'blockquote') continue;

    const text = (el.textContent || '').trim();
    if (!hasVerseAnchor(text)) continue;

    // --- 1. Walk BACKWARD to absorb verse-continuation lines ---
    const verseEls = [el];
    let j = i - 1;
    while (j >= 0) {
      const prev = all[j];
      if (!prev || consumed.has(prev)) break;
      const ptag = (prev.tagName || '').toLowerCase();
      if (ptag !== 'p') break;
      const ptext = (prev.textContent || '').trim();
      if (!looksLikeVerseLine(ptext)) break;
      verseEls.unshift(prev);
      j -= 1;
    }

    // --- 2. Optional title — header OR <p><strong>label:</strong></p> immediately above ---
    let titleText = null;
    const beforeFirst = j; // index of element just above the verse block
    if (beforeFirst >= 0) {
      const cand = all[beforeFirst];
      if (cand && !consumed.has(cand)) {
        const ctag = (cand.tagName || '').toLowerCase();
        const ctext = (cand.textContent || '').trim();
        if (/^h[2-6]$/.test(ctag) && isVerseIntroHeader(ctext)) {
          titleText = ctext.replace(/:\s*$/, '');
          consumed.add(cand);
        } else if (ctag === 'p' && isVerseIntroHeader(ctext) && cand.querySelector('strong')) {
          // pattern: <p><strong>संस्कृत श्लोक:</strong></p>
          titleText = ctext.replace(/:\s*$/, '');
          consumed.add(cand);
        }
      }
    }

    // --- 3. Build the shloka block ---
    const shloka = doc.createElement('blockquote');
    shloka.className = 'shloka';

    if (titleText) {
      const t = doc.createElement('p');
      t.className = 'shloka-title';
      t.textContent = titleText;
      shloka.appendChild(t);
    }

    // Combine all verse lines into one .sanskrit paragraph with <br/>
    const sanskrit = doc.createElement('p');
    sanskrit.className = 'sanskrit';
    const innerParts = verseEls.map((v) => {
      let inner = v.innerHTML.trim();
      if (!/<br\s*\/?>/i.test(inner)) {
        inner = inner
          .replace(/।\s+(?=[\u0900-\u097F])/g, '।<br/>')
          .replace(/॥\s*$/g, '॥');
      }
      return inner;
    });
    sanskrit.innerHTML = innerParts.join('<br/>');
    shloka.appendChild(sanskrit);

    // --- 4. Look FORWARD to absorb meaning & source ---
    let k = i + 1;
    // 4a. Meaning header (h2-h6) — text only
    if (k < all.length) {
      const nxt = all[k];
      if (
        nxt &&
        /^h[2-6]$/i.test(nxt.tagName || '') &&
        /meaning|अर्थ|भावार्थ/i.test(nxt.textContent || '')
      ) {
        const follow = all[k + 1];
        if (follow && follow.tagName === 'P') {
          const m = doc.createElement('p');
          m.className = 'meaning';
          m.innerHTML = `<em>${follow.innerHTML.trim()}</em>`;
          shloka.appendChild(m);
          consumed.add(nxt);
          consumed.add(follow);
          k += 2;
        }
      } else if (nxt && nxt.tagName === 'P' && isMeaningLabel(nxt)) {
        // 4b. <p><strong>हिन्दी अर्थ:</strong> ...</p>  → use the full <p> as meaning
        const m = doc.createElement('p');
        m.className = 'meaning';
        // Strip the leading "label:" portion from the visible meaning to keep
        // the card clean — replace any leading <strong>…:</strong> with bold label.
        m.innerHTML = `<em>${nxt.innerHTML.trim()}</em>`;
        shloka.appendChild(m);
        consumed.add(nxt);
        k += 1;
      }
    }

    // 4c. Interpretation block — also absorb (gives editorial polish)
    if (k < all.length) {
      const nxt = all[k];
      if (nxt && nxt.tagName === 'P' && isInterpretationLabel(nxt)) {
        const interp = doc.createElement('p');
        interp.className = 'interpretation';
        interp.innerHTML = nxt.innerHTML.trim();
        shloka.appendChild(interp);
        consumed.add(nxt);
        k += 1;
      }
    }

    // 4d. Source — em-dash line
    if (k < all.length) {
      const nxt = all[k];
      if (nxt && nxt.tagName === 'P' && isSourceLine(nxt.textContent || '')) {
        const s = doc.createElement('p');
        s.className = 'source';
        s.textContent = (nxt.textContent || '').trim();
        shloka.appendChild(s);
        consumed.add(nxt);
        k += 1;
      }
    }

    // Replace the FIRST verse element with the shloka card, mark others consumed
    verseEls[0].parentNode.replaceChild(shloka, verseEls[0]);
    for (let v = 1; v < verseEls.length; v++) {
      if (verseEls[v].parentNode) verseEls[v].parentNode.removeChild(verseEls[v]);
      consumed.add(verseEls[v]);
    }
    consumed.add(el);
  }

  // Remove consumed elements still in DOM
  consumed.forEach((node) => {
    if (node && node.parentNode) {
      try { node.parentNode.removeChild(node); } catch { /* already removed */ }
    }
  });

  return root.innerHTML;
}

export default enhanceShlokas;
